/**
 * The Settings panel for one storage node: a single draft covering identity
 * (code/name), "Kept at", zone, slot naming and parent, turned into the
 * sequence of API calls a save needs. Pure so it is covered: this repo's
 * vitest only collects `src/**\/*.test.ts`.
 *
 * Three different endpoints back one form — `updateNode`, `relabel` and
 * `moveNode` — so a save is a short plan (`settingsOps`) run one step at a
 * time (`runSettingsOps`) rather than a single request. Stopping at the first
 * failure matters here: a renamed node whose relabel then fails must not be
 * reported as unsaved, and the move after it must not run on a layout that
 * never finished changing.
 */

import type { ApiStorageNode, SlotLabelScheme, StorageNodeKind } from '@/types/storage'
import type { UpdateStorageNodePayload } from '@/api/storageApi'
import { DEFAULT_LABEL_PREFIX, gridCellLabels } from '@/lib/slotLabels'
import { isAtOrUnder, isOccupiable } from '@/lib/storageTree'

/**
 * 'INHERIT' reads as "take it from the unit above" for both Kept at and Zone.
 *
 * 'LEGACY' is Kept at set directly (`providesCondition`, no profile). It is
 * only ever the value a draft opens with: reading it as 'INHERIT' would make
 * "Same as …" look already chosen, and a save could never tell "left alone"
 * from "chose to inherit" — which clears the direct value.
 */
export type NodeSettingsDraft = {
  code: string
  name: string
  keptAt: 'INHERIT' | 'LEGACY' | string
  zoneId: 'INHERIT' | string
  /** Null when the node has no grid — there is nothing to relabel. */
  scheme: SlotLabelScheme | null
  prefix: string
  parentId: string | null
}

/** The Kept-at value a node has now, in draft terms. */
function keptAtOf(node: ApiStorageNode): NodeSettingsDraft['keptAt'] {
  return node.storageProfileId ?? (node.providesCondition ? 'LEGACY' : 'INHERIT')
}

/** The draft a Settings panel opens with, read straight off the node. */
export function draftFromNode(node: ApiStorageNode): NodeSettingsDraft {
  return {
    code: node.code,
    name: node.name ?? '',
    keptAt: keptAtOf(node),
    zoneId: node.zoneId ?? 'INHERIT',
    scheme: node.labelScheme,
    prefix: node.labelPrefix ?? '',
    parentId: node.parentId,
  }
}

/** One request a save may need to make. */
export type SettingsOp =
  | { kind: 'update'; patch: UpdateStorageNodePayload }
  | { kind: 'relabel'; scheme: SlotLabelScheme; prefix?: string }
  | { kind: 'move'; newParentId: string | null }

function slotNamingChanged(node: ApiStorageNode, draft: NodeSettingsDraft): boolean {
  if (draft.scheme === null) return false
  if (draft.scheme !== node.labelScheme) return true
  return draft.scheme === 'SEQUENTIAL' && draft.prefix.trim() !== (node.labelPrefix ?? '')
}

/**
 * The draft's changes, as the ordered list of requests a save makes:
 * `update` first (identity, Kept at, zone — one PATCH), then `relabel`, then
 * `move`. A field that did not change is left out of `update.patch` entirely,
 * not sent back unchanged.
 */
export function settingsOps(node: ApiStorageNode, draft: NodeSettingsDraft): SettingsOp[] {
  const ops: SettingsOp[] = []

  const patch: UpdateStorageNodePayload = {}
  const code = draft.code.trim()
  if (code !== node.code) patch.code = code

  const name = draft.name.trim()
  if (name !== (node.name ?? '')) patch.name = name === '' ? null : name

  // 'LEGACY' is never a choice, only the starting point, so it never patches.
  if (draft.keptAt !== keptAtOf(node) && draft.keptAt !== 'LEGACY') {
    patch.storageProfileId = draft.keptAt === 'INHERIT' ? null : draft.keptAt
  }

  if (draft.zoneId !== (node.zoneId ?? 'INHERIT')) {
    patch.zoneId = draft.zoneId === 'INHERIT' ? null : draft.zoneId
  }

  if (Object.keys(patch).length > 0) ops.push({ kind: 'update', patch })

  if (slotNamingChanged(node, draft)) {
    const scheme = draft.scheme as SlotLabelScheme
    const prefix = draft.prefix.trim()
    ops.push(scheme === 'SEQUENTIAL' && prefix !== '' ? { kind: 'relabel', scheme, prefix } : { kind: 'relabel', scheme })
  }

  if (draft.parentId !== node.parentId) ops.push({ kind: 'move', newParentId: draft.parentId })

  return ops
}

/** How many distinct fields the draft changed — what "N change(s) not saved" counts. */
export function changeCount(node: ApiStorageNode, draft: NodeSettingsDraft): number {
  let count = 0
  if (draft.code.trim() !== node.code) count++
  if (draft.name.trim() !== (node.name ?? '')) count++
  if (draft.keptAt !== keptAtOf(node) && draft.keptAt !== 'LEGACY') count++
  if (draft.zoneId !== (node.zoneId ?? 'INHERIT')) count++
  if (slotNamingChanged(node, draft)) count++
  if (draft.parentId !== node.parentId) count++
  return count
}

/** What a save ended up doing: every op that completed, and the one that didn't. */
export type SaveOutcome = {
  done: SettingsOp[]
  failed: { op: SettingsOp; message: string } | null
}

/**
 * Runs `ops` one at a time, in order, stopping at the first failure.
 *
 * The panel needs this distinction to avoid claiming "saved" on a partial
 * result: `done` is what to drop from the draft, `failed` is what to keep
 * dirty and show the server's message on.
 */
export async function runSettingsOps(
  ops: SettingsOp[],
  run: (op: SettingsOp) => Promise<void>,
): Promise<SaveOutcome> {
  const done: SettingsOp[] = []
  for (const op of ops) {
    try {
      await run(op)
      done.push(op)
    } catch (err) {
      return { done, failed: { op, message: err instanceof Error ? err.message : String(err) } }
    }
  }
  return { done, failed: null }
}

/**
 * Only what the user changed, keyed by draft field. The form shows
 * `applyEdits(node, edits)`, so a field nobody touched always reads the node
 * as it now is — including straight after a save refetches it.
 */
export type SettingsEdits = Partial<NodeSettingsDraft>

export function applyEdits(node: ApiStorageNode, edits: SettingsEdits): NodeSettingsDraft {
  return { ...draftFromNode(node), ...edits }
}

const PATCH_FIELD: Record<keyof UpdateStorageNodePayload, keyof NodeSettingsDraft | null> = {
  code: 'code',
  name: 'name',
  storageProfileId: 'keptAt',
  zoneId: 'zoneId',
  providesCondition: null,
  active: null,
}

/**
 * The edits left once `done` has saved: every field a completed op carried is
 * dropped, so it reads the refetched node, and the rest stay dirty. This is
 * what keeps a half-finished save (rename saved, relabel refused) from either
 * claiming everything saved or re-sending what already went through.
 */
export function dropSaved(edits: SettingsEdits, done: SettingsOp[]): SettingsEdits {
  const next: SettingsEdits = { ...edits }
  for (const op of done) {
    if (op.kind === 'update') {
      for (const key of Object.keys(op.patch) as (keyof UpdateStorageNodePayload)[]) {
        const field = PATCH_FIELD[key]
        if (field) delete next[field]
      }
    } else if (op.kind === 'relabel') {
      delete next.scheme
      delete next.prefix
    } else {
      delete next.parentId
    }
  }
  return next
}

/**
 * One line naming what the grid's slots would be called — "A1 · A2 · A3 … C3"
 * — minted by the same function the server uses. Null when the prefix is one
 * the server would refuse.
 */
export function slotNamePreview({
  rows,
  columns,
  scheme,
  prefix,
  childKind,
}: {
  rows: number
  columns: number
  scheme: SlotLabelScheme
  prefix: string
  childKind: StorageNodeKind
}): string | null {
  try {
    const codes = gridCellLabels({
      rows,
      columns,
      scheme,
      prefix: prefix.trim() || DEFAULT_LABEL_PREFIX[childKind] || 'N',
    }).map((c) => c.code)
    const head = codes.slice(0, 3).join(' · ')
    return codes.length > 3 ? `${head} … ${codes[codes.length - 1]}` : head
  } catch {
    return null
  }
}

/**
 * The zone the last node of `chain` (root→node) would take from above, named
 * after the ancestor that actually sets it — the "Same as <name> (<zone>)"
 * choice, like `inheritedKeptAt`. With nothing set above it is named after the
 * parent. Null for a unit, which has nothing above it.
 */
export function inheritedZone(chain: ApiStorageNode[]): { zoneId: string | null; fromName: string } | null {
  if (chain.length < 2) return null
  for (let i = chain.length - 2; i >= 0; i--) {
    const setter = chain[i]
    if (setter.zoneId) return { zoneId: setter.zoneId, fromName: setter.name ?? setter.code }
  }
  const parent = chain[chain.length - 2]
  return { zoneId: null, fromName: parent.name ?? parent.code }
}

/**
 * Where a node may move to: any container in the unit other than its current
 * parent, itself or anything beneath it (that would detach the branch from
 * every root). A slot holds stock rather than children.
 */
export function moveTargets<T extends ApiStorageNode>(node: ApiStorageNode, nodes: T[]): T[] {
  return nodes.filter(
    (n) => n.id !== node.id && n.id !== node.parentId && !isOccupiable(n.kind) && !isAtOrUnder(n.path, node.path),
  )
}
